import {
  Button,
  Card,
  Collapse,
  Drawer,
  Empty,
  Form,
  Input,
  notification,
  Select,
  Tag,
} from "antd";
import cuid from "cuid";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiPlus } from "react-icons/bi";
import {
  toPricerDateForm,
  toPricerDatePayload,
} from "screens/Private/Admin/Pricers/RateLookup/form";
import DatePicker from "shared/components/Form/DatePicker";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  DATE_FORMAT,
  PRICER_TYPES,
  PRICING_MODES,
  TAG_COLORS,
} from "utils/constants";
import { toLimitInput } from "utils/helpers/coverageLimits";
import BenefitRow from "../Components/BenefitRow";
import PricingInput from "../Components/PricingInput";

const coveragePricerDateValue = (pricer) => {
  const range = toPricerDateForm(pricer);
  return range[0] && range[1] ? range : undefined;
};

const ConfigureBenefits = ({ values, setFieldValue, data }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);

  const benefitTypes = [
    {
      value: "PRIMARY_CARE",
      label: t("CONFIGURE.CONFIGURE_BENEFITS.BENEFIT_PRIMARY_CARE"),
    },
    {
      value: "SPECIALITY_CARE",
      label: t("CONFIGURE.CONFIGURE_BENEFITS.BENEFIT_SPECIALTY"),
    },
  ];

  const coverages = get(values, "insurancePlanCoverages") || [];
  const zones = get(values, "zones") || [];
  const ageBrackets = get(values, "ageBrackets") || [];
  const regimes = get(values, "regimes") || [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCovIndex, setDrawerCovIndex] = useState(null);
  const [form] = Form.useForm();

  const { data: benefitsData } = useQuery(graphql.queries.COVERAGE_BENEFITS, {
    variables: {},
  });
  const { data: planChoices } = useQuery(
    graphql.queries.INSURANCE_PLAN_CHOICES,
    {
      variables: {
        where: { isStandalone: false },
        orderBy: [{ name: "ASC" }],
      },
    },
  );
  const upsellPlans = (get(planChoices, "insurancePlans.data") || []).filter(
    (plan) => plan.id !== get(data, "id") && plan.isStandalone === false,
  );

  const [createBenefit, { loading: creating }] = useMutation(
    graphql.mutations.CREATE_COVERAGE_BENEFIT,
    {
      refetchQueries: [
        { query: graphql.queries.COVERAGE_BENEFITS, variables: {} },
      ],
      awaitRefetchQueries: true,
    },
  );

  const [addMapping] = useMutation(graphql.mutations.CREATE_COVERAGE_MAPPING);
  const [deleteMapping] = useMutation(
    graphql.mutations.DELETE_COVERAGE_MAPPING,
  );
  const [updateMapping] = useMutation(
    graphql.mutations.UPDATE_COVERAGE_MAPPING,
  );
  const [createPricer] = useMutation(graphql.mutations.CREATE_PRICER);
  const [updatePricer] = useMutation(graphql.mutations.UPDATE_PRICER);

  const allBenefits = get(benefitsData, "coverageBenefits.data") || [];

  const handleCreateBenefit = () => {
    form.validateFields().then(async (formValues) => {
      const benefitId = cuid();
      const benefit = {
        id: benefitId,
        type: formValues.type,
        subtype: formValues.subtype || null,
        requirement: formValues.requirement || null,
        additionalInfo: formValues.additionalInfo || null,
        order: parseInt(formValues.order, 10) || 1,
      };

      try {
        await createBenefit({ variables: { data: benefit } });
        form.resetFields();
        setDrawerOpen(false);

        if (drawerCovIndex !== null) {
          await handleAddBenefit(drawerCovIndex, benefit);
        }
      } catch (err) {
        notification.error({
          message: t("CONFIGURE.NOTIFICATIONS.BENEFIT_CREATE_FAILED"),
        });
      }
    });
  };

  const handleAddBenefit = async (covIndex, benefit) => {
    const coverage = coverages[covIndex];
    const mappingId = cuid();

    try {
      await addMapping({
        variables: {
          data: {
            id: mappingId,
            waitingDays: 0,
            insurancePlanCoverage: { id: coverage.id },
            coverage: { id: benefit.id },
          },
        },
      });
      const newMapping = {
        id: mappingId,
        waitingDays: 0,
        coverage: benefit,
        limits: [],
        supportingInformation: [],
        pricer: null,
      };
      const updatedMappings = [
        ...(get(coverage, "coverageMappings") || []),
        newMapping,
      ];
      setFieldValue(
        `insurancePlanCoverages.${covIndex}.coverageMappings`,
        updatedMappings,
      );
      notification.success({
        message: t("CONFIGURE.NOTIFICATIONS.BENEFIT_ADDED", {
          type: benefit.type,
        }),
      });
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.BENEFIT_ADD_FAILED"),
      });
    }
  };

  const handleRemoveBenefit = async (covIndex, mapIndex) => {
    const coverage = coverages[covIndex];
    const mapping = (get(coverage, "coverageMappings") || [])[mapIndex];

    try {
      await deleteMapping({
        variables: { where: { id: mapping.id } },
      });
      const updatedMappings = (get(coverage, "coverageMappings") || []).filter(
        (_, i) => i !== mapIndex,
      );
      setFieldValue(
        `insurancePlanCoverages.${covIndex}.coverageMappings`,
        updatedMappings,
      );
      notification.success({
        message: t("CONFIGURE.NOTIFICATIONS.BENEFIT_REMOVED"),
      });
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.DELETE_FAILED"),
      });
    }
  };

  const handleUpdateMappingField = async (
    covIndex,
    mapIndex,
    field,
    value,
    localOnly,
  ) => {
    const mapping = (get(coverages[covIndex], "coverageMappings") || [])[
      mapIndex
    ];
    if (!mapping) return;

    if (field === "_batch" && typeof value === "object") {
      for (const [k, v] of Object.entries(value)) {
        setFieldValue(
          `insurancePlanCoverages.${covIndex}.coverageMappings.${mapIndex}.${k}`,
          v,
        );
      }
      try {
        await updateMapping({
          variables: {
            where: { id: mapping.id },
            data: value,
          },
        });
      } catch (err) {
        notification.error({
          message: t("CONFIGURE.NOTIFICATIONS.UPDATE_FAILED"),
        });
      }
      return;
    }

    setFieldValue(
      `insurancePlanCoverages.${covIndex}.coverageMappings.${mapIndex}.${field}`,
      value,
    );

    if (localOnly) return;

    try {
      await updateMapping({
        variables: {
          where: { id: mapping.id },
          data: { [field]: value },
        },
      });
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.UPDATE_FAILED"),
      });
    }
  };

  const handleUpdateLimits = async (
    covIndex,
    mapIndex,
    nextLimits,
    persist,
  ) => {
    setFieldValue(
      `insurancePlanCoverages.${covIndex}.coverageMappings.${mapIndex}.limits`,
      nextLimits,
    );
    if (!persist) return;

    const mapping = (get(coverages[covIndex], "coverageMappings") || [])[
      mapIndex
    ];
    if (!mapping) return;

    try {
      await updateMapping({
        variables: {
          where: { id: mapping.id },
          data: { limits: nextLimits.map(toLimitInput) },
        },
      });
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.UPDATE_FAILED"),
      });
    }
  };

  const [updateCoverageObj] = useMutation(
    graphql.mutations.UPDATE_INSURANCE_PLAN_COVERAGE,
  );

  const ensurePricer = async (existingPricer, prices, dates = {}) => {
    const existingId = get(existingPricer, "id");
    const planId = get(data, "id");
    if (existingId) {
      await updatePricer({
        variables: {
          where: { id: existingId },
          data: { prices, ...dates },
        },
      });
      return { ...existingPricer, prices, ...dates };
    }

    const newId = cuid();
    await createPricer({
      variables: {
        data: {
          id: newId,
          type: PRICER_TYPES.COMPOSABLE,
          name: "auto",
          prices,
          ...dates,
          ...(planId ? { insurancePlan: { id: planId } } : {}),
        },
      },
    });
    return { id: newId, type: PRICER_TYPES.COMPOSABLE, prices, ...dates };
  };

  const handleUpdateBenefitPricer = async (covIndex, mapIndex, prices) => {
    const mapping = (get(coverages[covIndex], "coverageMappings") || [])[
      mapIndex
    ];

    try {
      const pricer = await ensurePricer(get(mapping, "pricer"), prices);
      await updateMapping({
        variables: {
          where: { id: mapping.id },
          data: { pricer: { id: pricer.id } },
        },
      });
      setFieldValue(
        `insurancePlanCoverages.${covIndex}.coverageMappings.${mapIndex}.pricer`,
        pricer,
      );
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.PRICER_UPDATE_FAILED"),
      });
    }
  };

  const handleUpdateCoveragePricer = async (covIndex, patch) => {
    const coverage = coverages[covIndex];
    const prices =
      patch.prices !== undefined
        ? patch.prices
        : get(coverage, "pricer.prices");
    const dates = {};
    if ("startDate" in patch) dates.startDate = patch.startDate;
    if ("endDate" in patch) dates.endDate = patch.endDate;

    try {
      const pricer = await ensurePricer(get(coverage, "pricer"), prices, dates);
      await updateCoverageObj({
        variables: {
          where: { id: coverage.id },
          data: { pricer: { id: pricer.id } },
        },
      });
      setFieldValue(`insurancePlanCoverages.${covIndex}.pricer`, pricer);
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.COVERAGE_PRICER_UPDATE_FAILED"),
      });
    }
  };

  const handleUpdateCoverageField = async (
    covIndex,
    field,
    value,
    localOnly,
  ) => {
    setFieldValue(`insurancePlanCoverages.${covIndex}.${field}`, value);

    if (localOnly) return;

    const coverage = coverages[covIndex];
    try {
      await updateCoverageObj({
        variables: {
          where: { id: coverage.id },
          data: { [field]: value },
        },
      });
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.UPDATE_FAILED"),
      });
    }
  };

  if (coverages.length === 0) {
    return (
      <div className="configure-step">
        <Empty
          description={t("CONFIGURE.CONFIGURE_BENEFITS.EMPTY_COVERAGES")}
        />
      </div>
    );
  }

  const collapseItems = coverages.map((coverage, covIndex) => {
    const category = get(coverage, "coverageCategory", {});
    const covPricingMode =
      get(coverage, "pricer.type") === PRICER_TYPES.COMPOSABLE
        ? PRICING_MODES.COMPOSABLE
        : PRICING_MODES.PACKAGED;
    const mappings = get(coverage, "coverageMappings") || [];
    const selectedBenefitIds = mappings.map((m) => get(m, "coverage.id"));
    const availableBenefits = allBenefits.filter(
      (b) => !selectedBenefitIds.includes(b.id),
    );

    return {
      key: coverage.id,
      label: (
        <div className="d-flex justify--between w-100">
          <div className="d-flex gap-8 align--center">
            {category.icon && (
              <img
                src={category.icon}
                alt=""
                className="configure-category-icon"
              />
            )}
            <strong>{category.label || category.value}</strong>
            <Tag>
              {t("CONFIGURE.CONFIGURE_BENEFITS.BENEFITS_COUNT", {
                count: mappings.length,
              })}
            </Tag>
          </div>
          <Tag
            color={
              covPricingMode === PRICING_MODES.COMPOSABLE
                ? TAG_COLORS.GREEN
                : TAG_COLORS.BLUE
            }
          >
            {covPricingMode === PRICING_MODES.COMPOSABLE
              ? t("CONFIGURE.TAGS.COMPOSABLE")
              : t("CONFIGURE.TAGS.PACKAGED")}
          </Tag>
        </div>
      ),
      children: (
        <div>
          {(covPricingMode === PRICING_MODES.PACKAGED ||
            Boolean(get(coverage, "pricer.prices.rates"))) && (
            <Card size="small" className="m-bottom-16">
              <div className="d-flex flex--column gap-16">
                <strong>
                  {covPricingMode === PRICING_MODES.COMPOSABLE
                    ? t(
                        "CONFIGURE.CONFIGURE_BENEFITS.COVERAGE_PRICE_COMPOSABLE",
                      )
                    : t("CONFIGURE.CONFIGURE_BENEFITS.COVERAGE_PRICE_PACKAGED")}
                </strong>
                <DatePicker
                  name={`coverage-pricer-dates-${coverage.id}`}
                  placeholder={t("CONFIGURE.CONFIGURE_BENEFITS.PRICER_PERIOD")}
                  placeholders={[
                    t("CONFIGURE.CONFIGURE_BENEFITS.PRICER_START"),
                    t("CONFIGURE.CONFIGURE_BENEFITS.PRICER_END"),
                  ]}
                  format={DATE_FORMAT}
                  value={coveragePricerDateValue(get(coverage, "pricer"))}
                  onChange={(range) =>
                    handleUpdateCoveragePricer(
                      covIndex,
                      toPricerDatePayload(range),
                    )
                  }
                />
                <PricingInput
                  value={get(coverage, "pricer.prices")}
                  zones={zones}
                  ageBrackets={ageBrackets}
                  regimes={regimes}
                  onChange={(prices) =>
                    handleUpdateCoveragePricer(covIndex, { prices })
                  }
                />
              </div>
            </Card>
          )}

          <div>
            <div className="d-flex justify--between m-bottom-12">
              <h4>{t("CONFIGURE.CONFIGURE_BENEFITS.BENEFITS")}</h4>
              <div className="d-flex gap-8">
                <Select
                  placeholder={t("CONFIGURE.CONFIGURE_BENEFITS.ADD_EXISTING")}
                  className="configure-field--select"
                  showSearch
                  optionFilterProp="children"
                  value={null}
                  onChange={(benefitId) => {
                    const benefit = allBenefits.find((b) => b.id === benefitId);
                    if (benefit) handleAddBenefit(covIndex, benefit);
                  }}
                >
                  {availableBenefits.map((b) => (
                    <Select.Option key={b.id} value={b.id}>
                      {b.type}
                      {b.subtype ? ` - ${b.subtype}` : ""}
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  icon={<BiPlus size={16} />}
                  onClick={() => {
                    setDrawerCovIndex(covIndex);
                    setDrawerOpen(true);
                  }}
                >
                  {t("CONFIGURE.CONFIGURE_BENEFITS.NEW")}
                </Button>
              </div>
            </div>

            {mappings.length === 0 ? (
              <Empty
                description={t("CONFIGURE.CONFIGURE_BENEFITS.EMPTY_BENEFITS")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <div className="configure-stack configure-stack--sm">
                {mappings.map((mapping, mapIndex) => (
                  <BenefitRow
                    key={mapping.id}
                    mapping={mapping}
                    zones={zones}
                    ageBrackets={ageBrackets}
                    regimes={regimes}
                    showPricing={covPricingMode === PRICING_MODES.COMPOSABLE}
                    onRemove={() => handleRemoveBenefit(covIndex, mapIndex)}
                    onFieldChange={(field, value, localOnly) =>
                      handleUpdateMappingField(
                        covIndex,
                        mapIndex,
                        field,
                        value,
                        localOnly,
                      )
                    }
                    onPricerChange={(prices) =>
                      handleUpdateBenefitPricer(covIndex, mapIndex, prices)
                    }
                    onLimitsChange={(nextLimits, persist) =>
                      handleUpdateLimits(
                        covIndex,
                        mapIndex,
                        nextLimits,
                        persist,
                      )
                    }
                    upsellPlans={upsellPlans}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ),
    };
  });

  return (
    <div className="configure-step">
      <Collapse
        items={collapseItems}
        defaultActiveKey={coverages.map((c) => c.id)}
      />

      <Drawer
        title={t("CONFIGURE.CONFIGURE_BENEFITS.NEW_BENEFIT")}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={400}
        extra={
          <Button
            type="primary"
            onClick={handleCreateBenefit}
            loading={creating}
          >
            {t("CONFIGURE.CONFIGURE_BENEFITS.CREATE")}
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="type"
            label={t("CONFIGURE.CONFIGURE_BENEFITS.TYPE")}
            rules={[
              {
                required: true,
                message: t("CONFIGURE.CONFIGURE_BENEFITS.TYPE_REQUIRED"),
              },
            ]}
          >
            <Select
              placeholder={t("CONFIGURE.CONFIGURE_BENEFITS.TYPE_PLACEHOLDER")}
            >
              {benefitTypes.map((benefitType) => (
                <Select.Option
                  key={benefitType.value}
                  value={benefitType.value}
                >
                  {benefitType.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="subtype"
            label={t("CONFIGURE.CONFIGURE_BENEFITS.SUBTYPE")}
          >
            <Input placeholder="e.g. Orthodontics, Implants..." />
          </Form.Item>
          <Form.Item
            name="requirement"
            label={t("CONFIGURE.CONFIGURE_BENEFITS.REQUIREMENT")}
          >
            <Input placeholder="e.g. Referral required" />
          </Form.Item>
          <Form.Item
            name="additionalInfo"
            label={t("CONFIGURE.CONFIGURE_BENEFITS.ADDITIONAL_INFO")}
          >
            <Input.TextArea rows={2} placeholder="Any extra details" />
          </Form.Item>
          <Form.Item
            name="order"
            label={t("CONFIGURE.CONFIGURE_BENEFITS.DISPLAY_ORDER")}
          >
            <Input type="number" placeholder="1" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default ConfigureBenefits;
